import React, { useEffect, useMemo, useState } from 'react';
import {
  Grid, Paper, Box, Typography, Button, IconButton, Divider,
  TextField, CircularProgress, Table, TableBody, TableHead, TableRow, TableCell, TableContainer, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemIcon, ListItemText,
  TableSortLabel, Tooltip, Alert
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudIcon from '@mui/icons-material/Cloud';
import PeopleIcon from '@mui/icons-material/People';
import ConsumptionIcon from '@mui/icons-material/TrendingUp';
import AlertIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import SpeedIcon from '@mui/icons-material/Speed';
import CloseIcon from '@mui/icons-material/Close';
import StatCard from './StatCard';
import { getCloud, refreshCloud, deleteCloud } from '../api/clouds';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip, Legend } from 'recharts';

const PRIMARY = '#054693';

function toFixedSafe(value, digits = 2) {
  const num = Number(value);
  return Number.isFinite(num) ? num.toFixed(digits) : (0).toFixed(digits);
}

export default function CloudDetailsView({ cloudId, onRefresh, onDelete, onUpdateCredentials }) {
  const [cloudDetail, setCloudDetail] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [searchSku, setSearchSku] = useState('');
  const [alertsDialogOpen, setAlertsDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'sku', direction: 'asc' });
  const [connectionError, setConnectionError] = useState('');

  useEffect(() => {
    if (cloudId) {
      loadDetail(cloudId);
    }
  }, [cloudId]);

  async function loadDetail(id) {
    setActionLoading(true);
    try {
      const detail = await getCloud(id);
      setCloudDetail(detail);
      setConnectionError('');
    } catch (err) {
      console.error('Error loading cloud detail:', err);
      setConnectionError(err?.response?.data?.message || 'Could not load this connection. Try again.');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleSync(id) {
    setActionLoading(true);
    setConnectionError('');
    try {
      await refreshCloud(id);
      await loadDetail(id);
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Sync error:', err);
      await loadDetail(id);
      if (onRefresh) await onRefresh();
      setConnectionError(err?.response?.data?.message || 'Refresh failed. Check the connection settings and try again.');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleDelete(id) {
    setDeleteDialogOpen(true);
  }

  async function confirmDelete() {
    try {
      await deleteCloud(cloudDetail.id);
      setDeleteDialogOpen(false);
      if (onDelete) onDelete();
    } catch (err) {
      console.error('Delete error:', err);
    }
  }

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const summary = cloudDetail?.summary || {};
  const provider = cloudDetail?.provider;

  // Enhanced alerts logic for M365/Azure - Must be declared before any conditional returns
  const enhancedAlerts = useMemo(() => {
    if (!cloudDetail || provider === 'aws' || !summary.licenses) return [];
    
    const alerts = [];
    const licenses = summary.licenses || [];

    licenses.forEach(lic => {
      const assigned = parseInt(lic.assigned, 10) || 0;
      const enabled = parseInt(lic.enabled, 10) || 0;
      const consumed = Math.max(assigned, enabled);
      
      if (consumed === 0) {
        alerts.push({
          title: 'Unused License',
          message: `${lic.sku} has ${lic.prepaid} licenses but none are assigned. Consider removing this license to save costs.`,
          severity: 'error',
          action: 'Review License Usage'
        });
      } else if (consumed < lic.prepaid * 0.7) {
        const waste = lic.prepaid - consumed;
        alerts.push({
          title: 'Low Utilization',
          message: `${lic.sku} has ${waste} unused licenses (${Math.round((consumed / lic.prepaid) * 100)}% utilization).`,
          severity: 'warning',
          action: 'Optimize License Count'
        });
      }
    });

    return alerts;
  }, [summary.licenses, provider]);

  // Filtered and sorted licenses - Must be declared before any conditional returns
  const filteredLicenses = useMemo(() => {
    if (!cloudDetail || !summary.licenses) return [];
    
    let filtered = summary.licenses.filter(lic => 
      lic.sku.toLowerCase().includes(searchSku.toLowerCase())
    );
    
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        
        if (sortConfig.key === 'assigned' || sortConfig.key === 'enabled' || sortConfig.key === 'prepaid') {
          aVal = parseInt(aVal, 10) || 0;
          bVal = parseInt(bVal, 10) || 0;
        }
        
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return filtered;
  }, [cloudDetail, summary.licenses, searchSku, sortConfig]);

  const costData = useMemo(() => {
    if (!cloudDetail) return [];
    return summary.cost?.timeSeries?.map((item, idx) => ({
      date: item.date || `Day ${idx + 1}`,
      cost: parseFloat(item.cost || 0)
    })) || [];
  }, [cloudDetail, summary.cost]);

  // Early return after all hooks
  if (!cloudDetail) {
    return (
      <Box sx={{ p: 6, textAlign: 'center' }}>
        {connectionError ? <Alert severity="error">{connectionError}</Alert> : <CircularProgress />}
        {!connectionError && <Typography variant="body2" sx={{ mt: 2 }}>Loading cloud data...</Typography>}
      </Box>
    );
  }

  return (
    <Box>
      {(connectionError || cloudDetail?.summary?.error) && <Alert severity="error" sx={{ mb: 2 }}>{connectionError || cloudDetail.summary.error}</Alert>}
      {onUpdateCredentials && <Button size="small" onClick={onUpdateCredentials} sx={{ mb: 2 }}>Update credentials</Button>}
      {/* Cloud Platform Header */}
      <Paper sx={{ p: { xs: 2, sm: 2.5, md: 3 }, mb: 3, border: '1px solid #E2E8F0', borderRadius: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap={{ xs: 'wrap', sm: 'nowrap' }} gap={2}>
          <Box display="flex" alignItems="center" gap={2} flex={1}>
            {/* Cloud Platform Logo */}
            <Box
              component="img"
              src={
                cloudDetail.provider?.toLowerCase() === 'm365' ? '/m365.jpg' : 
                cloudDetail.provider?.toLowerCase() === 'aws' ? '/aws.png' : 
                '/azure.jpg'
              }
              alt={cloudDetail.provider?.toUpperCase()}
              sx={{
                width: { xs: 40, sm: 48 },
                height: { xs: 40, sm: 48 },
                borderRadius: 2,
                objectFit: 'cover',
                border: '2px solid white',
                boxShadow: 2
              }}
            />
            
            <Box flex={1}>
              <Typography variant="h5" sx={{ fontWeight: 800, color: PRIMARY, mb: 0.5, fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' } }}>
                {cloudDetail.provider?.toUpperCase() === 'M365' ? 'Microsoft 365' : 
                 cloudDetail.provider?.toUpperCase() === 'AWS' ? 'Amazon Web Services' : 
                 'Microsoft Azure'} Dashboard
              </Typography>
              
              <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 2 }} flexWrap="wrap">
                <Chip
                  size="small"
                  label={String(cloudDetail.status || '').toLowerCase() === 'active' ? 'Connected' : 'Needs attention'}
                  color={String(cloudDetail.status || '').toLowerCase() === 'active' ? 'success' : 'error'}
                  variant="outlined"
                />
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  Last successful sync: {summary.fetchedAt ? new Date(summary.fetchedAt).toLocaleString() : 'Never'}
                </Typography>
                
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' }, display: { xs: 'none', sm: 'block' } }}>
                  {cloudDetail.provider === 'aws' ? `Region: ${cloudDetail.awsRegion || 'us-east-1'}` : `Tenant: ${cloudDetail.tenantId}`}
                </Typography>
              </Box>
            </Box>
          </Box>
          
          {/* Action Buttons */}
          <Box display="flex" gap={1}>
            <Tooltip title="Refresh cloud data">
              <IconButton 
                onClick={() => handleSync(cloudDetail.id)} 
                disabled={actionLoading}
                sx={{ 
                  bgcolor: 'white', 
                  boxShadow: 1,
                  '&:hover': { 
                    bgcolor: 'grey.50',
                    transform: 'scale(1.05)' 
                  },
                  transition: 'all 0.2s'
                }}
              >
                <RefreshIcon sx={{ color: PRIMARY }} />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Remove cloud connection">
              <IconButton 
                onClick={() => handleDelete(cloudDetail.id)} 
                disabled={actionLoading}
                sx={{ 
                  bgcolor: 'white', 
                  boxShadow: 1,
                  '&:hover': { 
                    bgcolor: '#fee',
                    transform: 'scale(1.05)' 
                  },
                  transition: 'all 0.2s'
                }}
              >
                <DeleteIcon sx={{ color: '#d32f2f' }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* Statistics Cards */}
      <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} sx={{ mb: 3 }}>
        {cloudDetail.provider === 'aws' ? (
          // AWS-specific stats
          <>
            <Grid item xs={6} sm={6} md={2.4}>
              <StatCard
                label="EC2 Instances"
                value={summary.ec2?.total || 0}
                caption={`${summary.ec2?.running || 0} running • ${summary.ec2?.stopped || 0} stopped`}
                icon={<CloudIcon sx={{ color: PRIMARY }} />}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={2.4}>
              <StatCard
                label="S3 Buckets"
                value={summary.s3?.total || 0}
                caption={`${summary.s3?.totalSizeGB || 0} GB total`}
                icon={<ConsumptionIcon sx={{ color: PRIMARY }} />}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={2.4}>
              <StatCard
                label="RDS Databases"
                value={summary.rds?.total || 0}
                caption="Database instances"
                icon={<CloudIcon sx={{ color: PRIMARY }} />}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={2.4}>
              <StatCard
                label="Lambda Functions"
                value={summary.lambda?.total || 0}
                caption="Serverless functions"
                icon={<SpeedIcon sx={{ color: PRIMARY }} />}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={2.4}>
              <StatCard
                label="Alerts"
                value={summary.alerts?.length || 0}
                caption="Cost & security"
                icon={<AlertIcon sx={{ color: summary.alerts?.length > 0 ? '#f57c00' : PRIMARY }} />}
                onClick={() => setAlertsDialogOpen(true)}
                clickable={true}
              />
            </Grid>
          </>
        ) : (
          // M365/Azure license-based stats
          <>
            <Grid item xs={6} sm={6} md={3}>
              <StatCard
                label="Total Licenses"
                value={summary.totalLicenses || 0}
                caption="All subscriptions"
                icon={<CloudIcon sx={{ color: PRIMARY }} />}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <StatCard
                label="Assigned"
                value={summary.totalAssigned || 0}
                caption={`${summary.totalLicenses ? Math.round((summary.totalAssigned / summary.totalLicenses) * 100) : 0}% utilized`}
                icon={<PeopleIcon sx={{ color: PRIMARY }} />}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <StatCard
                label="Available"
                value={summary.totalAvailable || 0}
                caption="Unused licenses"
                icon={<TrendingDownIcon sx={{ color: summary.totalAvailable > summary.totalLicenses * 0.3 ? '#f57c00' : PRIMARY }} />}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <StatCard
                label="Alerts"
                value={enhancedAlerts.length}
                caption="Optimization opportunities"
                icon={<AlertIcon sx={{ color: enhancedAlerts.length > 0 ? '#f57c00' : PRIMARY }} />}
                onClick={() => setAlertsDialogOpen(true)}
                clickable={true}
              />
            </Grid>
          </>
        )}
      </Grid>

      {/* AWS Analytics Summary */}
      {cloudDetail.provider === 'aws' && summary.analytics && (
        <Paper sx={{ p: 3, mb: 3, border: '1px solid #E2E8F0', borderRadius: 2, bgcolor: '#FFFFFF' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: PRIMARY }}>
            Cost Analytics & Insights
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Projected Monthly Cost</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#0F172A' }}>
                  ${toFixedSafe(summary.analytics.projectedMonthlyCost, 2)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Total Resources</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#0F172A' }}>
                  {summary.analytics.totalResources || 0}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Cost Optimization</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#0F172A' }}>
                  {summary.analytics.costOptimizationOpportunities || 0} opportunities
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Security Issues</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#0F172A' }}>
                  {summary.analytics.securityIssues || 0}
                </Typography>
              </Box>
            </Grid>
          </Grid>
          {summary.analytics.topSpendingServices?.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Top Spending Services</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {summary.analytics.topSpendingServices.slice(0, 5).map((service, idx) => (
                  <Chip 
                    key={idx}
                    label={`${service.service}: $${toFixedSafe(service.cost, 2)}`}
                    variant="outlined"
                    sx={{ fontWeight: 500 }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Paper>
      )}

      {/* AWS Resource Tables */}
      {cloudDetail.provider === 'aws' && (
        <>
          {/* EC2 Instances */}
          {summary.ec2?.instances?.length > 0 && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: PRIMARY }}>
                EC2 Instances ({summary.ec2.total})
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Instance ID</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>State</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Region</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {summary.ec2.instances.slice(0, 10).map((instance, idx) => (
                      <TableRow key={idx}>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{instance.instanceId}</TableCell>
                        <TableCell>{instance.name || '—'}</TableCell>
                        <TableCell>{instance.instanceType}</TableCell>
                        <TableCell>
                          <Chip 
                            label={instance.state}
                            size="small"
                            color={instance.state === 'running' ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell>{instance.region}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* S3 Buckets */}
          {summary.s3?.buckets?.length > 0 && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: PRIMARY }}>
                S3 Buckets ({summary.s3.total})
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Bucket Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Region</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Size (GB)</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {summary.s3.buckets.slice(0, 10).map((bucket, idx) => (
                      <TableRow key={idx}>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{bucket.name}</TableCell>
                        <TableCell>{bucket.region || '—'}</TableCell>
                        <TableCell>{toFixedSafe(bucket.sizeGB, 2)}</TableCell>
                        <TableCell>{bucket.creationDate ? new Date(bucket.creationDate).toLocaleDateString() : '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}

      {/* License Table for M365/Azure */}
      {(cloudDetail.provider === 'm365' || cloudDetail.provider === 'azure') && summary.licenses && (
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: PRIMARY }}>
              License Details ({filteredLicenses.length})
            </Typography>
            <TextField
              size="small"
              placeholder="Search by SKU..."
              value={searchSku}
              onChange={(e) => setSearchSku(e.target.value)}
              sx={{ minWidth: { xs: '100%', sm: 250 } }}
            />
          </Box>
          
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell>
                    <TableSortLabel
                      active={sortConfig.key === 'sku'}
                      direction={sortConfig.direction}
                      onClick={() => handleSort('sku')}
                      sx={{ fontWeight: 600 }}
                    >
                      License SKU
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortConfig.key === 'prepaid'}
                      direction={sortConfig.direction}
                      onClick={() => handleSort('prepaid')}
                      sx={{ fontWeight: 600 }}
                    >
                      Prepaid
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortConfig.key === 'assigned'}
                      direction={sortConfig.direction}
                      onClick={() => handleSort('assigned')}
                      sx={{ fontWeight: 600 }}
                    >
                      Assigned
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortConfig.key === 'enabled'}
                      direction={sortConfig.direction}
                      onClick={() => handleSort('enabled')}
                      sx={{ fontWeight: 600 }}
                    >
                      Enabled
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Available</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Utilization</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredLicenses.map((lic, idx) => {
                  const assigned = parseInt(lic.assigned, 10) || 0;
                  const enabled = parseInt(lic.enabled, 10) || 0;
                  const consumed = Math.max(assigned, enabled);
                  const available = lic.prepaid - consumed;
                  const utilization = lic.prepaid > 0 ? (consumed / lic.prepaid) * 100 : 0;
                  const isLowUtil = utilization < 70;
                  const isUnused = consumed === 0;
                  
                  return (
                    <TableRow 
                      key={idx}
                      sx={{ 
                        bgcolor: isUnused ? '#ffebee' : isLowUtil ? '#fff3e0' : 'inherit',
                        '&:hover': { bgcolor: isUnused ? '#ffcdd2' : isLowUtil ? '#ffe0b2' : '#f5f5f5' }
                      }}
                    >
                      <TableCell sx={{ fontWeight: 500 }}>{lic.sku}</TableCell>
                      <TableCell align="right">{lic.prepaid}</TableCell>
                      <TableCell align="right">{lic.assigned}</TableCell>
                      <TableCell align="right">{lic.enabled}</TableCell>
                      <TableCell align="right">
                        <Chip 
                          label={available}
                          size="small"
                          color={available > lic.prepaid * 0.3 ? 'warning' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${Math.round(utilization)}%`}
                          size="small"
                          color={isUnused ? 'error' : isLowUtil ? 'warning' : 'success'}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* AWS Cost Chart */}
      {cloudDetail.provider === 'aws' && costData.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: PRIMARY }}>
            Cost Trend (Last 30 Days)
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={costData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <ReTooltip />
              <Legend />
              <Bar dataKey="cost" fill={PRIMARY} name="Daily Cost ($)" />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* Alerts Dialog */}
      <Dialog 
        open={alertsDialogOpen} 
        onClose={() => setAlertsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {cloudDetail?.provider === 'aws' ? 'AWS Alerts & Recommendations' : 'License Alerts & Recommendations'}
            </Typography>
            <IconButton onClick={() => setAlertsDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {!cloudDetail ? (
            <Box textAlign="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (cloudDetail.provider === 'aws' ? (summary.alerts?.length || 0) : enhancedAlerts.length) === 0 ? (
            <Box textAlign="center" py={4}>
              <AlertIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" color="success.main">All Good!</Typography>
              <Typography variant="body2" color="text.secondary">
                No alerts or recommendations at this time.
              </Typography>
            </Box>
          ) : (
            <List>
              {(cloudDetail.provider === 'aws' ? (summary.alerts || []) : enhancedAlerts).filter(alert => alert && (alert.title || alert.type) && (alert.message || alert.msg)).map((alert, index) => {
                const alertType = alert.type || 'info';
                const alertTitle = alert.title || alert.type || 'Alert';
                const alertMessage = alert.message || alert.msg || '';
                const isError = alertType.toLowerCase().includes('critical') || alertType.toLowerCase().includes('security') || alertType.toLowerCase().includes('waste');
                const isWarning = alertType.toLowerCase().includes('cost') || alertType.toLowerCase().includes('optimization') || alertType.toLowerCase().includes('warning');
                
                return (
                  <ListItem key={index} divider>
                    <ListItemIcon>
                      {isError || alert.severity === 'error' ? (
                        <ErrorIcon color="error" />
                      ) : isWarning || alert.severity === 'warning' ? (
                        <AlertIcon color="warning" />
                      ) : alert.severity === 'success' ? (
                        <AlertIcon color="success" />
                      ) : (
                        <AlertIcon color="info" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{alertTitle}</Typography>}
                      secondary={
                        <>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{alertMessage}</Typography>
                          {alert.action && (
                            <Chip label={alert.action} size="small" color="primary" variant="outlined" />
                          )}
                        </>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAlertsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove this cloud connection? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
